/**
 * CPF, CNPJ e WhatsApp como o banco confere no autocadastro (0150: `app.documento_valido`,
 * `app.whatsapp_normalizado`). Aqui servem só para a tela avisar antes de enviar —
 * quem decide é o servidor, e as duas contas precisam dar o mesmo resultado.
 *
 * Desde julho de 2026 a Receita emite CNPJ alfanumérico: as 12 primeiras posições
 * aceitam letra, os dois dígitos verificadores continuam numéricos, e cada posição
 * vale o código ASCII menos 48.
 */

export function documentoLimpo(valor: string): string {
    return String(valor ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

function digitoDoCnpj(base: string, pesos: number[]): number {
    let soma = 0;
    for (let i = 0; i < pesos.length; i += 1) soma += ((base.charCodeAt(i) || 48) - 48) * (pesos[i] ?? 0);
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
}

export function documentoValido(valor: string): boolean {
    const d = documentoLimpo(valor);
    if (/^\d{11}$/.test(d)) {
        if (/^(\d)\1{10}$/.test(d)) return false;
        const conta = (ate: number) => {
            let soma = 0;
            for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
            const r = (soma * 10) % 11;
            return r === 10 ? 0 : r;
        };
        return conta(9) === Number(d[9]) && conta(10) === Number(d[10]);
    }
    if (/^[0-9A-Z]{12}\d{2}$/.test(d)) {
        if (/^(\d)\1{13}$/.test(d)) return false;
        return digitoDoCnpj(d, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(d[12])
            && digitoDoCnpj(d, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]) === Number(d[13]);
    }
    return false;
}

export function documentoFormatado(valor: string): string {
    const d = documentoLimpo(valor);
    if (d.length === 11) return d.replace(/^(.{3})(.{3})(.{3})(.{2})$/, '$1.$2.$3-$4');
    if (d.length === 14) return d.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5');
    return d;
}

/** 55 + DDD + número, ou nulo. Aceita com ou sem o 55, fixo ou celular. */
export function whatsappNormalizado(valor: string): string | null {
    let d = String(valor ?? '').replace(/\D/g, '');
    if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
    return /^[1-9][1-9](9\d{8}|[2-8]\d{7})$/.test(d) ? `55${d}` : null;
}

/** `(64) 99912-3456`, para mostrar o que foi guardado. */
export function whatsappFormatado(valor: string): string {
    const n = whatsappNormalizado(valor);
    if (!n) return String(valor ?? '');
    const d = n.slice(2);
    return d.length === 11 ? d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3') : d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
}
